import React, { useCallback, useEffect, useState } from "react";
import {
  decidePasswordRecoveryRequest,
  fetchPendingPasswordRecoveryRequests,
} from "../api/client";

const RECOVERY_API = {
  decidePasswordRecoveryRequest,
  fetchPendingPasswordRecoveryRequests,
};

export default function PasswordRecoveryRequests({ api = RECOVERY_API }) {
  const [requests, setRequests] = useState([]);
  const [codes, setCodes] = useState({});
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async () => {
    try {
      setRequests(await api.fetchPendingPasswordRecoveryRequests());
      setError("");
    } catch (err) {
      setError(err.message || "Password recovery requests are unavailable.");
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (item, decision) => {
    setBusyId(item.request_id);
    setError("");
    setNotice("");
    try {
      const result = await api.decidePasswordRecoveryRequest(item.request_id, decision);
      if (result.status === "APPROVED" && result.token) {
        setCodes((current) => ({ ...current, [item.request_id]: result.token }));
        setNotice(`Recovery approved for ${item.login}. Deliver the displayed code directly after verifying the person's identity in person.`);
      } else if (result.status === "REJECTED") {
        setNotice(`Recovery request for ${item.login} was rejected or the account was not eligible.`);
      } else if (result.status === "EXPIRED") {
        setNotice("The recovery request expired and must be submitted again.");
      } else {
        setError("The recovery decision returned an invalid status.");
      }
      await refresh();
    } catch (err) {
      setError(err.message || "The recovery decision could not be recorded.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="studio-panel iam-pending-panel" aria-labelledby="iam-recovery-title">
      <div className="studio-panel-header">
        <div>
          <div className="studio-eyebrow">ACCOUNT RECOVERY</div>
          <h2 id="iam-recovery-title">Password recovery requests ({requests.length})</h2>
        </div>
        <button className="studio-btn-ghost" type="button" onClick={refresh}>Refresh</button>
      </div>
      <p className="studio-muted">
        After approval, verify the requester in person and hand them the code directly.
        It is single-use, expires in 20 minutes, and is shown only once. Do not send it by
        email/chat or capture it in a screenshot.
      </p>
      {notice && <p className="studio-alert" role="status">{notice}</p>}
      {error && <p className="studio-alert studio-alert-attention" role="alert">{error}</p>}
      {Object.entries(codes).map(([requestId, code]) => (
        <div className="studio-alert recovery-handoff-code" key={requestId} role="status">
          <strong>One-time code — deliver in person:</strong>
          <code>{code}</code>
          <button className="studio-btn-ghost" type="button"
            onClick={() => setCodes((current) => {
              const remaining = { ...current };
              delete remaining[requestId];
              return remaining;
            })}>
            Dismiss code
          </button>
        </div>
      ))}
      {!requests.length && !error && <p className="studio-muted">There are no recovery requests awaiting review.</p>}
      {requests.map((item) => (
        <article className="iam-pending-row" key={item.request_id}>
          <div>
            <strong>{item.login}</strong>
            <div className="studio-muted">Requested {new Date(item.requested_at).toLocaleString()}</div>
          </div>
          <div className="iam-pending-actions">
            <button type="button" className="studio-btn-primary"
              disabled={!!busyId}
              onClick={() => decide(item, "APPROVE")}>
              {busyId === item.request_id ? "Saving…" : "Approve & issue code"}
            </button>
            <button type="button" className="studio-btn-ghost" disabled={!!busyId}
              onClick={() => decide(item, "REJECT")}>Reject</button>
          </div>
        </article>
      ))}
    </section>
  );
}
